import React, { useEffect, useMemo, useState } from 'react';
import { isEmpty, keyBy, startCase } from 'lodash';

import useFetchAdjusters from '~/Adjuster/useFetchAdjusters';
import { useClaim } from '~/components/ClaimContainer';
import type { NotificationsType } from '~/components/ClaimNotificationsCard/types';
import { ToggleSeenNotifications } from '~/components/ClaimNotificationsContainer';
import { MainCard } from '~/components/core';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import InputAdornment from '~/components/core/Atomic/InputAdornment';
import CategoryFilter from '~/components/core/Molecules/CategoryFilter/CategoryFilter';
import DatePickerField from '~/components/core/Molecules/Fields/DatePickerField';
import MultiSelectField from '~/components/core/Molecules/Fields/MultiSelectField';
import LabeledValue from '~/components/core/Molecules/LabledValue';
import { useCms } from '~/components/hooks/useCms';
import CloseIcon_Deprecated from '~/components/icons/CloseIcon_Deprecated';
import { useLazyNotificationTypeCount } from '~/components/Notifications/hooks/useLazyNotificationTypeCount';
import useOrganization from '~/components/OrganizationContext';
import SubOrganizationMultiSelect from '~/components/TPA/SubOrganizations/SubOrganizationMultiSelect';
import CLAIM_NOTIFICATIONS_PRIORITY from '~/server_shared/generated-types/CLAIM_NOTIFICATIONS_PRIORITY';
import type { Category } from '~/server_shared/swagger/data-contracts';
import { NOTIFICATION_STATUSES } from '~/Types';

export interface NotificationsFiltersValues {
  sub_organization_ids: number[];
  categories_ids: number[];
  statuses: (keyof typeof NOTIFICATION_STATUSES)[];
  types: (NotificationsType | 'All')[];
  priorities: (keyof typeof CLAIM_NOTIFICATIONS_PRIORITY)[];
  start_due_date: string | null;
  end_due_date: string | null;
  adjusters_ids: string[];
}

interface NotificationsFiltersProps {
  onSearch: (currentFields: NotificationsFiltersValues) => void;
  isUpdating: boolean;
  defaultAssignee: string[];
}

const NotificationsFilters: React.FC<NotificationsFiltersProps> = ({ onSearch, isUpdating, defaultAssignee }) => {
  const { notificationCategories, isLoadingNotificationCategories, isErrorNotificationCategories, organizationId } =
    useOrganization() as {
      notificationCategories: Category[];
      isLoadingNotificationCategories: boolean;
      isErrorNotificationCategories: boolean;
      organizationId: number;
    };

  const { user } = useCms();
  const { claim: claimInContext } = useClaim(); // might not be in claim context

  const {
    isLoading: isLoadingPossibleTypes,
    loadIfNeeded: loadPossibleTypesIfNeeded,
    notificationTypeToTypeConfig,
    sortedPossibleNotificationTypes: possibleTypes,
  } = useLazyNotificationTypeCount(organizationId, claimInContext?.id);

  const { isLoadingPossibleAdjusters, isErrorPossibleAdjusters, possibleAdjustersWithoutDaSpecialist } =
    useFetchAdjusters(user.organization_id);

  const [selectedSubOrgIds, setSelectedSubOrgIds] = useState<number[]>([]);
  const [selectedCategoriesIds, setSelectedCategoriesIds] = useState<number[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<(keyof typeof NOTIFICATION_STATUSES)[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<(NotificationsType | 'All')[]>([]);
  const [selectedPriorities, setSelectedPriorities] = useState<(keyof typeof CLAIM_NOTIFICATIONS_PRIORITY)[]>([]);
  const [selectedStartDueDate, setSelectedStartDueDate] = useState<string | null>(null);
  const [selectedEndDueDate, setSelectedEndDueDate] = useState<string | null>(null);
  const [selectedAdjustersIds, setSelectedAdjustersIds] = useState<string[]>(defaultAssignee);
  const [startDueDateError, setStartDueDateError] = useState<string>('');
  const [endDueDateError, setEndDueDateError] = useState<string>('');

  if (user.id && !possibleAdjustersWithoutDaSpecialist?.some((adjuster) => adjuster.id === user.id)) {
    possibleAdjustersWithoutDaSpecialist.push({
      id: user.id,
      username: user.username,
      role_type: user.role?.name,
      sub_organization_ids: user.sub_organization_ids,
      licenses: [],
      is_sys_config_editor: user.is_sys_config_editor || false,
      title: user?.title || '',
      email: user?.email || '',
      is_removed: user.is_removed || false,
      is_active: user.is_active,
    });
  }

  const allowedAdjusters = useMemo(
    () =>
      possibleAdjustersWithoutDaSpecialist.filter(
        (adjuster) =>
          user.is_org_level_supervisor ||
          user.all_subordinates_recursive_users_ids?.includes(adjuster.id) ||
          user.shared_notifications_user_ids?.includes(adjuster.id) ||
          user.id === adjuster.id
      ),
    [
      possibleAdjustersWithoutDaSpecialist,
      user.is_org_level_supervisor,
      user.all_subordinates_recursive_users_ids,
      user.shared_notifications_user_ids,
      user.id,
    ]
  );
  const adjustersById = keyBy(allowedAdjusters, 'id');

  useEffect(() => {
    if (!selectedAdjustersIds.length && user) {
      setSelectedAdjustersIds([user.id]);
    }
  }, [user, selectedAdjustersIds]);

  useEffect(() => {
    if (!isLoadingPossibleAdjusters && !isErrorPossibleAdjusters && possibleAdjustersWithoutDaSpecialist) {
      onSearch({
        sub_organization_ids: selectedSubOrgIds,
        categories_ids: selectedCategoriesIds,
        statuses: selectedStatuses,
        types: selectedTypes?.includes('All') ? selectedTypes.filter((type) => type !== 'All') : selectedTypes,
        priorities: selectedPriorities,
        start_due_date: selectedStartDueDate,
        end_due_date: selectedEndDueDate,
        adjusters_ids: selectedAdjustersIds.includes('All') ? [] : selectedAdjustersIds,
      });
    }
  }, [
    onSearch,
    selectedSubOrgIds,
    selectedCategoriesIds,
    selectedStatuses,
    selectedTypes,
    selectedPriorities,
    selectedStartDueDate,
    selectedEndDueDate,
    selectedAdjustersIds,
    isLoadingPossibleAdjusters,
    isErrorPossibleAdjusters,
    possibleAdjustersWithoutDaSpecialist,
    allowedAdjusters,
  ]);

  return (
    <div>
      <MainCard type="filled" collapsible title="Filter" openByDefault={false} className="mb-12">
        <div className="grid grid-cols-3 gap-x-20 gap-y-32">
          <SubOrganizationMultiSelect onChange={setSelectedSubOrgIds} disabled={isUpdating} />
          <div>
            <CategoryFilter<Category>
              categories={notificationCategories}
              selectedCategories={selectedCategoriesIds}
              onChange={setSelectedCategoriesIds}
              disabled={isUpdating}
              isLoading={isLoadingNotificationCategories}
              isError={isErrorNotificationCategories}
            />
          </div>
          <div>
            <MultiSelectField<keyof typeof NOTIFICATION_STATUSES>
              id="status"
              label="Status"
              options={Object.keys(NOTIFICATION_STATUSES) as (keyof typeof NOTIFICATION_STATUSES)[]}
              disabled={isUpdating}
              renderValue={(selectedStatuses: (keyof typeof NOTIFICATION_STATUSES)[]) => {
                return selectedStatuses.map((selectedStatus) => NOTIFICATION_STATUSES[selectedStatus] || '').join(', ');
              }}
              renderOption={(statusKey: keyof typeof NOTIFICATION_STATUSES) => {
                return NOTIFICATION_STATUSES[statusKey] || '';
              }}
              fullWidth
              sortAlphabetic
              onChange={setSelectedStatuses}
              value={selectedStatuses}
              InputProps={{
                endAdornment: isEmpty(selectedStatuses) ? null : (
                  <InputAdornment className="mr-24" position="start">
                    <IconButton className="p-8" onClick={() => setSelectedStatuses([])}>
                      <CloseIcon_Deprecated size={10} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>
          <div>
            <MultiSelectField<NotificationsType | 'All', 'All'>
              id="type"
              label="Types"
              onFocus={loadPossibleTypesIfNeeded}
              value={selectedTypes}
              onChange={setSelectedTypes}
              renderValue={(selectedTypes) =>
                selectedTypes
                  ?.map((id) => {
                    const typeConfig = notificationTypeToTypeConfig[id];
                    return typeConfig?.subtype
                      ? `${typeConfig.label} - ${startCase(typeConfig.subtype.replace('_', ' '))}`
                      : typeConfig?.label;
                  })
                  ?.join(', ')
              }
              renderOption={(possibleNotificationTypeId) => {
                const possibleNotificationType = notificationTypeToTypeConfig[possibleNotificationTypeId];
                return (
                  <div className="flex items-center gap-12">
                    {possibleNotificationType?.icon}
                    <LabeledValue
                      label={possibleNotificationType?.label}
                      value={
                        possibleNotificationType?.subtype &&
                        startCase(possibleNotificationType.subtype.replace('_', ' '))
                      }
                    />
                  </div>
                );
              }}
              options={possibleTypes}
              optionsLoading={isLoadingPossibleTypes}
              addAllOption
              allOptionValue="All"
              isAllOptionChecksAllCheckboxes
              fullWidth
              InputProps={{
                endAdornment: isEmpty(selectedAdjustersIds) ? null : (
                  <InputAdornment className="mr-24" position="start">
                    <IconButton className="p-8" onClick={() => setSelectedTypes([])}>
                      <CloseIcon_Deprecated size={10} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>
          <div>
            <MultiSelectField<keyof typeof CLAIM_NOTIFICATIONS_PRIORITY>
              id="priority"
              label="Priority"
              options={Object.keys(CLAIM_NOTIFICATIONS_PRIORITY) as (keyof typeof CLAIM_NOTIFICATIONS_PRIORITY)[]}
              disabled={isUpdating}
              renderValue={(selectedPriorityKeys: (keyof typeof CLAIM_NOTIFICATIONS_PRIORITY)[]) => {
                return selectedPriorityKeys
                  .map((selectedPriorityKey) => CLAIM_NOTIFICATIONS_PRIORITY[selectedPriorityKey] || '')
                  .join(', ');
              }}
              renderOption={(priorityKey: keyof typeof CLAIM_NOTIFICATIONS_PRIORITY) => {
                return CLAIM_NOTIFICATIONS_PRIORITY[priorityKey] || '';
              }}
              fullWidth
              sortAlphabetic
              onChange={setSelectedPriorities}
              value={selectedPriorities}
              InputProps={{
                endAdornment: isEmpty(selectedPriorities) ? null : (
                  <InputAdornment className="mr-24" position="start">
                    <IconButton className="p-8" onClick={() => setSelectedPriorities([])}>
                      <CloseIcon_Deprecated size={10} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>
          <div>
            <DatePickerField
              value={selectedStartDueDate}
              onChange={(date) => {
                if (date && date.format('YYYY-MM-DD') === 'Invalid date') {
                  setStartDueDateError('Invalid date format');
                  return;
                }
                setStartDueDateError('');
                setSelectedStartDueDate(date ? date.format('YYYY-MM-DD') : null);
              }}
              label="Start Due Date"
              fullWidth
              clearable
              error={!isEmpty(startDueDateError)}
              helperText={startDueDateError}
            />
          </div>
          <div>
            <DatePickerField
              value={selectedEndDueDate}
              onChange={(date) => {
                if (date && date.format('YYYY-MM-DD') === 'Invalid date') {
                  setEndDueDateError('Invalid date format');
                  return;
                }
                setEndDueDateError('');
                setSelectedEndDueDate(date ? date.format('YYYY-MM-DD') : null);
              }}
              label="End Due Date"
              fullWidth
              clearable
              error={!isEmpty(endDueDateError)}
              helperText={endDueDateError}
            />
          </div>
          {!isLoadingPossibleAdjusters && !isErrorPossibleAdjusters && allowedAdjusters.length > 0 && (
            <div>
              <MultiSelectField<string, 'All'>
                id="adjusters_ids"
                label="Assignee"
                options={Object.keys(adjustersById)}
                disabled={isUpdating}
                renderValue={(selectedAdjustersIds) => {
                  return selectedAdjustersIds
                    .map((selectedAdjusterId) =>
                      selectedAdjusterId === 'All' ? 'All' : adjustersById[selectedAdjusterId].username
                    )
                    .join(', ');
                }}
                renderOption={(adjusterId) => adjustersById[adjusterId].username}
                fullWidth
                sortAlphabetic
                onChange={setSelectedAdjustersIds}
                value={selectedAdjustersIds}
                addAllOption
                allOptionValue="All"
                InputProps={{
                  endAdornment: isEmpty(selectedAdjustersIds) ? null : (
                    <InputAdornment className="mr-24" position="start">
                      <IconButton className="p-8" onClick={() => setSelectedAdjustersIds([])}>
                        <CloseIcon_Deprecated size={10} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </div>
          )}
        </div>
      </MainCard>
      {claimInContext ? (
        <ToggleSeenNotifications
          showOnlyCurrentUserNotifications={
            selectedAdjustersIds?.includes(user.id) && selectedAdjustersIds.length === 1
          }
          setShowOnlyCurrentUserNotifications={(value) => setSelectedAdjustersIds(value ? [user.id] : ['All'])}
        />
      ) : null}
    </div>
  );
};

export default NotificationsFilters;
